import React, { useCallback, useMemo } from 'react';
import { LayoutDashboard } from 'lucide-react';
import { toast } from 'react-toastify';
import { t as toastT } from '../utils/translations';
import { cn, tableTheme as T, analysisTone } from './ui/analysisTableTheme.js';
import {
  rowMatchesTaxYear,
  formatTaxYearLabel,
} from '../utils/taxYear.js';
import {
  isGovernanceB3trRewardRow,
  isX2EarnB3trRewardRow,
  rewardAmountOf,
  rewardRecipientOf,
  rewardTimestampOf,
} from '../utils/multiWalletRewards.js';

function WalletPill({ address, onCopy, title }) {
  const short = `${address.slice(0, 8)}…${address.slice(-6)}`;

  return (
    <div
      className="group relative inline-flex max-w-full items-center gap-2 rounded-xl border border-slate-200/80 bg-white/90 px-3 py-2 shadow-sm ring-1 ring-white/70 backdrop-blur-[2px] transition-all duration-200 hover:border-slate-300 hover:shadow-md"
      title={title || address}
    >
      <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]">
        <span
          className="absolute -inset-y-3 -left-6 w-1/3 rotate-[18deg] bg-white/40 blur-md translate-x-[-120%] transition-transform duration-700 ease-out group-hover:translate-x-[220%]"
        />
      </span>

      <span className="truncate font-mono text-[12px] font-semibold text-slate-800">
        {short}
      </span>

      <button
        onClick={onCopy}
        type="button"
        className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-transparent text-slate-500 transition-all opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-700 active:scale-95"
        aria-label="Copy address"
        title="Adresse kopieren"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="block">
          <path d="M9 9h10v10H9z" stroke="currentColor" strokeWidth="1.5" />
          <path d="M5 5h10v10" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>
    </div>
  );
}

/**
 * 🚀 NEU: Multi-Wallet Rewards Overview Komponente
 * 
 * Zeigt eine konsolidierte Übersicht aller steuerpflichtigen Rewards pro Wallet:
 * - X2Earn Rewards / B3TR (kanonische Reward-Klassen + Legacy-Airdrops)
 * - Governance Rewards (gesamt & Jahr)
 * - VTHO Generated (gesamt & Jahr)
 * - Stargate Staking Rewards (gesamt & Jahr)
 * 
 * Arbeitet direkt mit klassifizierten Dataset-Daten ohne API-Calls.
 */
export default function MultiVthoOverview({
  year,
  walletSet,
  dataset,
  balanceVerifier, // Optional: Fallback für vthoGasFeesPerWallet
  selectedCountry = 'DE',
  language = 'de' // Default to German
}) {

  const displayYear = formatTaxYearLabel(year, selectedCountry);

  // 🌐 Übersetzungen
  const t = {
    de: {
      title: 'Rewards Overview (Multi-Wallet)',
      year: 'Jahr',
      wallets: 'Wallets',
      wallet: 'Wallet',
      airdropTotal: 'Airdrop (gesamt)',
      airdropYear: `Airdrop (${displayYear})`,
      governanceTotal: 'Governance (gesamt)',
      governanceYear: `Governance (${displayYear})`,
      vthoGenTotal: 'VTHO Generated (gesamt)',
      vthoGenYear: `VTHO Generated (${displayYear})`,
      stargateTotal: 'Stargate (gesamt)',
      stargateYear: `Stargate (${displayYear})`,
      total: 'Gesamt',
      noDataTitle: 'Keine Rewards-Daten verfügbar',
      noDataText: 'Für das ausgewählte Jahr und Wallet-Set wurden keine Rewards-Daten gefunden.',
      copyAddress: 'Adresse kopieren',
      governanceRewards: 'Governance-Rewards',
      generated: 'Generiert',
      gasFees: 'Gas-Gebühren',
      stargateStaking: 'Stargate Staking',
      x2earnB3trRewards: 'X2Earn Rewards',
      x2earnB3trMobileTotal: 'X2Earn Rewards (B3TR) gesamt',
      x2earnB3trMobileYear: `X2Earn Rewards (B3TR) (${displayYear})`,
    },
    en: {
      title: 'Rewards Overview (Multi-Wallet)',
      year: 'Year',
      wallets: 'Wallets',
      wallet: 'Wallet',
      airdropTotal: 'Airdrop (total)',
      airdropYear: `Airdrop (${displayYear})`,
      governanceTotal: 'Governance (total)',
      governanceYear: `Governance (${displayYear})`,
      vthoGenTotal: 'VTHO Generated (total)',
      vthoGenYear: `VTHO Generated (${displayYear})`,
      stargateTotal: 'Stargate (total)',
      stargateYear: `Stargate (${displayYear})`,
      total: 'Total',
      noDataTitle: 'No rewards data available',
      noDataText: 'No rewards data found for the selected year and wallet set.',
      copyAddress: 'Copy address',
      governanceRewards: 'Governance rewards',
      generated: 'Generated',
      gasFees: 'Gas fees',
      stargateStaking: 'Stargate Staking',
      x2earnB3trRewards: 'X2Earn Rewards',
      x2earnB3trMobileTotal: 'X2Earn Rewards (B3TR) total',
      x2earnB3trMobileYear: `X2Earn Rewards (B3TR) (${displayYear})`,
    },
  }[language] || {
    title: 'Rewards Overview (Multi-Wallet)',
    year: 'Jahr',
    wallets: 'Wallets',
    wallet: 'Wallet',
    airdropTotal: 'Airdrop (gesamt)',
    airdropYear: `Airdrop (${displayYear})`,
    governanceTotal: 'Governance (gesamt)',
    governanceYear: `Governance (${displayYear})`,
    vthoGenTotal: 'VTHO Generated (gesamt)',
    vthoGenYear: `VTHO Generated (${displayYear})`,
    stargateTotal: 'Stargate (gesamt)',
    stargateYear: `Stargate (${displayYear})`,
    total: 'Gesamt',
    noDataTitle: 'Keine Rewards-Daten verfügbar',
    noDataText: 'Für das ausgewählte Jahr und Wallet-Set wurden keine Rewards-Daten gefunden.',
    copyAddress: 'Adresse kopieren',
    governanceRewards: 'Governance-Rewards',
    generated: 'Generiert',
    gasFees: 'Gas-Gebühren',
    stargateStaking: 'Stargate Staking',
    x2earnB3trRewards: 'X2Earn Rewards',
    x2earnB3trMobileTotal: 'X2Earn Rewards (B3TR) gesamt',
    x2earnB3trMobileYear: `X2Earn Rewards (B3TR) (${displayYear})`,
  };

  const tone = analysisTone.indigo;

  // 🔧 Hilfsfunktion: Wallet-Adressen extrahieren (robust mit Fallbacks)
  const extractAddresses = (ws, ds) => {
    if (!ws) return [];
    
    const normalize = (s) => {
      if (!s) return null;
      let x = s.trim();
      if (!x.startsWith('0x') && /^[a-fA-F0-9]{40}$/.test(x)) x = '0x' + x;
      return x.toLowerCase();
    };
    
    const isHex40 = (s) => /^0x[a-fA-F0-9]{40}$/.test((s||'').trim());
    
    const allRaw = [
      ...(Array.isArray(ws?.addresses) ? ws.addresses : []),
      ...(Array.isArray(ws?.mainAddresses) ? ws.mainAddresses : []),
      ...(Array.isArray(ws?.delegateAddresses) ? ws.delegateAddresses : []),
      ...(Array.isArray(ws?.voteRewardAddresses) ? ws.voteRewardAddresses : [])
    ];

    const toAddr = (x) => typeof x === 'string' ? x : (x?.address || x?.main_wallet || x?.delegate_wallet || null);
    let mapped = allRaw.map(toAddr);
    
    // Fallbacks, wenn leer:
    if (!mapped.filter(Boolean).length) {
      if (ds?.vthoGeneratedPerWallet && typeof ds.vthoGeneratedPerWallet === 'object') {
        mapped = Object.keys(ds.vthoGeneratedPerWallet);
      } else if (Array.isArray(ds?.wallets)) {
        mapped = ds.wallets.map(w => w.main_wallet).filter(Boolean);
      }
    }
    
    const valid = mapped
      .filter(Boolean)
      .map(normalize)
      .filter(isHex40);
    return Array.from(new Set(valid));
  };

  // 🔧 Hilfsfunktion: Jahr-Filter (Timestamp in Sekunden)
  const isInYear = useCallback((timestamp, targetYear) => {
    if (!timestamp || !targetYear) return false;

    return rowMatchesTaxYear(
      { timestamp },
      selectedCountry,
      targetYear
    );
  }, [selectedCountry]);

  // 🔧 Berechne Rewards pro Wallet
  const { rows, totals } = useMemo(() => {
    if (!dataset || !walletSet) {
      return { rows: [], totals: null };
    }

    // 🧭 Debug-Logging (nur bei MULTIVTHO_DEBUG=1)
    const addresses = extractAddresses(walletSet, dataset);
    const addressSet = new Set(addresses);
    
    if (addresses.length === 0) {
      return { rows: [], totals: null };
    }

    // Initialize results per wallet
    const walletResults = {};
    addresses.forEach(addr => {
      const lowerAddr = addr.toLowerCase();
      walletResults[lowerAddr] = {
        address: addr,
        airdropTotal: 0,
        airdropYear: 0,
        govTotal: 0,
        govYear: 0,
        vthoGenTotal: 0,
        vthoGenYear: 0,
        gasFeeTotal: 0,
        gasFeeYear: 0,
        stargateTotal: 0,
        stargateYear: 0
      };
    });

    // 1. VTHO Generated aus vthoGeneratedPerWallet (NEU!)
    
    if (dataset.vthoGeneratedPerWallet && typeof dataset.vthoGeneratedPerWallet === 'object') {
      // Durchlaufe alle Wallets im AddressSet
      addresses.forEach(addr => {
        const lowerAddr = addr.toLowerCase();
        const genForWallet = dataset.vthoGeneratedPerWallet[lowerAddr];
        
        
        if (genForWallet) {
          // 🎯 Verwende Token-Anzahlen (VTHO), nicht EUR-Werte
          const totalVTHO = Number(genForWallet.totalVTHO || 0);
          const yearVTHO = Number(genForWallet.yearVTHO || 0);
          
          walletResults[lowerAddr].vthoGenTotal += totalVTHO;
          walletResults[lowerAddr].vthoGenYear += yearVTHO;
          
        }
      });
      
    }

    // 1.5. Stargate Staking Rewards aus vetVthoData (VTHO-Token mit stargate classification)
    
    if (dataset.vetVthoData && Array.isArray(dataset.vetVthoData)) {
      dataset.vetVthoData.forEach((row) => {
        const recipient = row.to_address?.toLowerCase() || row.to?.toLowerCase();
        const token = (row.token || row.token_type || row.symbol || '').toUpperCase();
        const classification = row.classification || '';
        
        
        if (!recipient || !addressSet.has(recipient)) return;
        if (token !== 'VTHO') return; // Nur VTHO-Transfers
        if (!classification.toLowerCase().includes('stargate') || !classification.toLowerCase().includes('staking')) return;
        
        const tokenAmount = parseFloat(row.amount || row.value || row.quantity) || 0;
        const isYearMatch = isInYear(row.timestamp || row.block_timestamp, year);
        
        walletResults[recipient].stargateTotal += tokenAmount;
        if (isYearMatch) {
          walletResults[recipient].stargateYear += tokenAmount;
        }
      });
    }

    // 1.6. VTHO Gas Fees (robust: number|string|object) + Year-Map beachten
    const feeTotalMap =
      dataset?.vthoGasFeesPerWallet ||
      balanceVerifier?.vthoGasFeesPerWallet ||
      {};

    const feeYearMap =
      dataset?.vthoGasFeesPerWalletYear ||
      balanceVerifier?.vthoGasFeesPerWalletYear ||
      {};

    const asNumber = (v) => {
      if (v == null) return 0;
      if (typeof v === "number") return v;
      if (typeof v === "string") return Number(v) || 0;
      if (typeof v === "object") {
        return Number(v.totalVTHO ?? v.yearVTHO ?? v.total ?? v.vtho ?? v.amount ?? 0) || 0;
      }
      return 0;
    };

    for (const addr of Object.keys(walletResults)) {
      const totalRaw = feeTotalMap[addr];
      const yearRaw = feeYearMap[addr];

      walletResults[addr].gasFeeTotal = asNumber(totalRaw);

      // Year bevorzugt aus vthoGasFeesPerWalletYear, sonst fallback auf Objektfeld
      walletResults[addr].gasFeeYear =
        asNumber(yearRaw) ||
        (typeof totalRaw === "object" ? asNumber(totalRaw.yearVTHO) : 0);
    }

    // 2. X2Earn B3TR & Governance B3TR aus vip180Data
    
    if (dataset.vip180Data && Array.isArray(dataset.vip180Data)) {
      dataset.vip180Data.forEach((row) => {
        const recipient = rewardRecipientOf(row);
        
        
        if (!recipient || !addressSet.has(recipient)) return;
        
        const tokenAmount = rewardAmountOf(row);
        const isYearMatch = isInYear(rewardTimestampOf(row), year);
        
        if (isX2EarnB3trRewardRow(row)) {
          walletResults[recipient].airdropTotal += tokenAmount;
          if (isYearMatch) {
            walletResults[recipient].airdropYear += tokenAmount;
          }
        } else if (isGovernanceB3trRewardRow(row)) {
          walletResults[recipient].govTotal += tokenAmount;
          if (isYearMatch) {
            walletResults[recipient].govYear += tokenAmount;
          }
        }
      });
    }

    // Convert to array and calculate totals
    const rows = Object.values(walletResults);
    
    
    const totals = {
      airdropTotal: rows.reduce((sum, r) => sum + r.airdropTotal, 0),
      airdropYear: rows.reduce((sum, r) => sum + r.airdropYear, 0),
      govTotal: rows.reduce((sum, r) => sum + r.govTotal, 0),
      govYear: rows.reduce((sum, r) => sum + r.govYear, 0),
      vthoGenTotal: rows.reduce((sum, r) => sum + r.vthoGenTotal, 0),
      vthoGenYear: rows.reduce((sum, r) => sum + r.vthoGenYear, 0),
      gasFeeTotal: rows.reduce((sum, r) => sum + r.gasFeeTotal, 0),
      gasFeeYear: rows.reduce((sum, r) => sum + r.gasFeeYear, 0),
      stargateTotal: rows.reduce((sum, r) => sum + r.stargateTotal, 0),
      stargateYear: rows.reduce((sum, r) => sum + r.stargateYear, 0)
    };


    return { rows, totals };
  }, [
    dataset,
    walletSet,
    year,
    balanceVerifier,
    isInYear,
  ]);

  // 🚀 FORMATIERUNG
  const formatTokens = (value) => {
    return Number(value).toLocaleString('de-DE', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const copyToClipboard = async (e, text) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
        try {
      await navigator.clipboard.writeText(text);
            toast.success(toastT('toast.addressCopied', language), {
        position: "top-right",
        autoClose: 3000,
      });
    } catch {
            toast.error(toastT('toast.copyFailed', language), {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };


  // 🚀 DEBUG: Zeige Dataset-Struktur

  // 🚀 NO DATA STATE
  if (!dataset || !rows.length) {
    return (
      <div className={T.shell}>
        <div className={cn(T.header, T.headerRail, tone.rail)}>
          <div className={T.headerInner}>
            <div className={T.headerMain}>
              <span className={T.eyebrow}>
                <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
                Multi-Wallet
              </span>
              <h3 className={T.title}>{t.noDataTitle}</h3>
              <p className={T.subtitle}>{t.noDataText}</p>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6">
          <div className={cn(T.detailCard, "text-xs text-slate-600")}>
            <strong className="text-slate-800">Debug Info:</strong>
            <ul className="mt-2 space-y-1">
              <li>Dataset: {dataset ? '✅' : '❌'}</li>
              <li>Dataset Keys: {dataset ? Object.keys(dataset).join(', ') : 'none'}</li>
              <li>vip180Data: {dataset?.vip180Data?.length || 0} items</li>
              <li>vthoTaxRows: {dataset?.vthoTaxRows?.length || 0} items</li>
              <li>Extracted Addresses: {extractAddresses(walletSet, dataset).length} items</li>
              <li>WalletSet Keys: {walletSet ? Object.keys(walletSet).join(', ') : 'none'}</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className={T.shell}>
      <div className={cn(T.header, T.headerRail, tone.rail)}>
        <div className={T.headerInner}>
          <div className={T.headerMain}>
            <span className={T.eyebrow}>
              <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
              Multi-Wallet
            </span>

            <h2 className={T.title}>{t.title}</h2>
            <p className={T.subtitle}>
              {language === 'en'
                ? 'Reward overview across all connected wallets.'
                : 'Reward-Übersicht über alle verbundenen Wallets.'}
            </p>
          </div>

          <div className={T.headerAsidePills}>
            <span className={T.countPill}>
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              {t.year} {displayYear}
            </span>

            <span className={T.countPill}>
              {rows.length} {t.wallets}
            </span>
          </div>
        </div>
      </div>

      {/* Table with clean design */}
      <div className={cn(T.tableBlock, "hidden md:block")}>
        <div className="v3-matrix-table-shell">
          <div className="overflow-x-auto overflow-y-hidden overscroll-x-contain rounded-[inherit]">
            <table className={cn(T.table, "w-full min-w-[1288px] table-fixed print:min-w-0")}>
              <thead className="sticky top-0 z-10">
                <tr className="v3-matrix-head-top border-b border-white/10 shadow-[inset_0_-1px_0_rgba(255,255,255,0.14)]">
                  <th
                    rowSpan={2}
                    className="w-[16%] px-6 py-4 text-left align-middle text-[11px] font-semibold uppercase tracking-[0.16em]
                               sticky left-0 z-20 border-r border-white/10 bg-[#2f55f5] text-white backdrop-blur-sm"
                  >
                    {t.wallet}
                  </th>

                  <th colSpan={2} className="px-4 py-3 text-center align-middle text-[11px] font-semibold uppercase tracking-[0.16em]">
                    {t.x2earnB3trRewards} (B3TR)
                  </th>

                  <th colSpan={2} className="px-4 py-3 text-center align-middle text-[11px] font-semibold uppercase tracking-[0.16em] border-l border-slate-200/80 whitespace-normal break-words leading-[1.15] [overflow-wrap:anywhere]">
                    {t.governanceRewards} (B3TR)
                  </th>

                  <th colSpan={2} className="px-4 py-3 text-center align-middle text-[11px] font-semibold uppercase tracking-[0.16em] border-l border-slate-200/80 whitespace-normal break-words leading-[1.15] [overflow-wrap:anywhere]">
                    {t.generated} (VTHO)
                  </th>

                  <th colSpan={2} className="px-4 py-3 text-center align-middle text-[11px] font-semibold uppercase tracking-[0.16em] border-l border-slate-200/80 whitespace-normal break-words leading-[1.15] [overflow-wrap:anywhere]">
                    {t.gasFees} (VTHO)
                  </th>

                  <th colSpan={2} className="px-4 py-3 text-center align-middle text-[11px] font-semibold uppercase tracking-[0.16em] border-l border-slate-200/80 whitespace-normal break-words leading-[1.15] [overflow-wrap:anywhere]">
                    {t.stargateStaking} (VTHO)
                  </th>
                </tr>

                <tr className="v3-matrix-head-sub border-b border-slate-200/80 shadow-[inset_0_-1px_0_rgba(148,163,184,0.12)]">
                  <th className="w-[8.4%] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] whitespace-normal break-words leading-[1.15]">
                    {language === 'en' ? 'Total' : 'Gesamt'}
                  </th>
                  <th className="w-[8.4%] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] whitespace-normal break-words leading-[1.15]">
                    ({displayYear})
                  </th>

                  <th className="w-[8.4%] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] border-l border-slate-200/80 whitespace-normal break-words leading-[1.15]">
                    {language === 'en' ? 'Total' : 'Gesamt'}
                  </th>
                  <th className="w-[8.4%] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] whitespace-normal break-words leading-[1.15]">
                    ({displayYear})
                  </th>

                  <th className="w-[8.4%] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] border-l border-slate-200/80 whitespace-normal break-words leading-[1.15]">
                    {language === 'en' ? 'Total' : 'Gesamt'}
                  </th>
                  <th className="w-[8.4%] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] whitespace-normal break-words leading-[1.15]">
                    ({displayYear})
                  </th>

                  <th className="w-[8.4%] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] border-l border-slate-200/80 whitespace-normal break-words leading-[1.15]">
                    {language === 'en' ? 'Total' : 'Gesamt'}
                  </th>
                  <th className="w-[8.4%] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] whitespace-normal break-words leading-[1.15]">
                    ({displayYear})
                  </th>

                  <th className="w-[8.4%] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] border-l border-slate-200/80 whitespace-normal break-words leading-[1.15]">
                    {language === 'en' ? 'Total' : 'Gesamt'}
                  </th>
                  <th className="w-[8.4%] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] whitespace-normal break-words leading-[1.15]">
                    ({displayYear})
                  </th>
                </tr>
              </thead>
              <tbody className={T.tbody}>
                {rows.map((r, idx) => {
                  const rowBg = idx % 2 === 0 ? "bg-white" : "bg-slate-50/45";
                  const stickyBg = idx % 2 === 0 ? "bg-white/95" : "bg-slate-50/95";

                  return (
                    <tr
                      key={r.address}
                      className={cn(
                        T.tr,
                        rowBg,
                        "group hover:bg-slate-100/70"
                      )}
                    >
                      <td
                        className={cn(
                          "px-6 py-4 sticky left-0 z-10 border-r border-slate-200/80 backdrop-blur-sm",
                          stickyBg
                        )}
                      >
                        <WalletPill
                          address={r.address}
                          onCopy={(e) => copyToClipboard(e, r.address)}
                          title={r.address}
                        />
                      </td>

                      <td className="px-4 py-4 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-800">
                        {formatTokens(r.airdropTotal)}
                      </td>
                      <td className="px-4 py-4 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-800 border-r border-slate-200/80">
                        {formatTokens(r.airdropYear)}
                      </td>

                      <td className="px-4 py-4 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-800">
                        {formatTokens(r.govTotal)}
                      </td>
                      <td className="px-4 py-4 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-800 border-r border-slate-200/80">
                        {formatTokens(r.govYear)}
                      </td>

                      <td className="px-4 py-4 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-800">
                        {formatTokens(r.vthoGenTotal)}
                      </td>
                      <td className="px-4 py-4 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-800 border-r border-slate-200/80">
                        {formatTokens(r.vthoGenYear)}
                      </td>

                      <td className="px-4 py-4 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-800">
                        {formatTokens(r.gasFeeTotal)}
                      </td>
                      <td className="px-4 py-4 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-800 border-r border-slate-200/80">
                        {formatTokens(r.gasFeeYear)}
                      </td>

                      <td className="px-4 py-4 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-800">
                        {formatTokens(r.stargateTotal)}
                      </td>
                      <td className="px-4 py-4 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-800">
                        {formatTokens(r.stargateYear)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-indigo-200/80 bg-gradient-to-r from-indigo-50 via-white to-violet-50/80 text-slate-900">
                  <td className="px-6 py-5 text-left sticky left-0 bg-indigo-50/95 border-r border-slate-200/80 z-10">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-200/80">📊</span>
                      {t.total}
                    </span>
                  </td>

                  <td className="px-4 py-5 text-right font-mono text-[13px] font-semibold">
                    {formatTokens(totals.airdropTotal)}
                  </td>
                  <td className="px-4 py-5 text-right font-mono text-[13px] font-semibold border-r border-slate-200/80">
                    {formatTokens(totals.airdropYear)}
                  </td>

                  <td className="px-4 py-5 text-right font-mono text-[13px] font-semibold">
                    {formatTokens(totals.govTotal)}
                  </td>
                  <td className="px-4 py-5 text-right font-mono text-[13px] font-semibold border-r border-slate-200/80">
                    {formatTokens(totals.govYear)}
                  </td>

                  <td className="px-4 py-5 text-right font-mono text-[13px] font-semibold">
                    {formatTokens(totals.vthoGenTotal)}
                  </td>
                  <td className="px-4 py-5 text-right font-mono text-[13px] font-semibold border-r border-slate-200/80">
                    {formatTokens(totals.vthoGenYear)}
                  </td>

                  <td className="px-4 py-5 text-right font-mono text-[13px] font-semibold">
                    {formatTokens(totals.gasFeeTotal)}
                  </td>
                  <td className="px-4 py-5 text-right font-mono text-[13px] font-semibold border-r border-slate-200/80">
                    {formatTokens(totals.gasFeeYear)}
                  </td>

                  <td className="px-4 py-5 text-right font-mono text-[13px] font-semibold">
                    {formatTokens(totals.stargateTotal)}
                  </td>
                  <td className="px-4 py-5 text-right font-mono text-[13px] font-semibold">
                    {formatTokens(totals.stargateYear)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <div className="md:hidden print:hidden px-3 py-4 space-y-4">
        {rows.map((r) => (
          <div
            key={`multi-${r.address}`}
            className="rounded-[1.2rem] border border-slate-200/80 bg-white/95 p-4 shadow-sm ring-1 ring-white/70 space-y-4"
          >
            <div className="flex items-center justify-between gap-3">
              <WalletPill
                address={r.address}
                onCopy={(e) => copyToClipboard(e, r.address)}
                title={r.address}
              />
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="space-y-1">
                <span className={T.mobileLabel}>{t.x2earnB3trMobileTotal}</span>
                <span className={cn(T.mono)}>{formatTokens(r.airdropTotal)}</span>
              </div>
              <div className="space-y-1 text-right">
                <span className={T.mobileLabel}>{t.x2earnB3trMobileYear}</span>
                <span className={cn(T.mono)}>{formatTokens(r.airdropYear)}</span>
              </div>

              <div className="space-y-1">
                <span className={T.mobileLabel}>B3TR Total</span>
                <span className={cn(T.mono)}>{formatTokens(r.govTotal)}</span>
              </div>
              <div className="space-y-1 text-right">
                <span className={T.mobileLabel}>B3TR ({displayYear})</span>
                <span className={cn(T.mono)}>{formatTokens(r.govYear)}</span>
              </div>

              <div className="space-y-1">
                <span className={T.mobileLabel}>VTHO Generated Total</span>
                <span className={cn(T.mono)}>{formatTokens(r.vthoGenTotal)}</span>
              </div>
              <div className="space-y-1 text-right">
                <span className={T.mobileLabel}>VTHO Generated ({displayYear})</span>
                <span className={cn(T.mono)}>{formatTokens(r.vthoGenYear)}</span>
              </div>

              <div className="space-y-1">
                <span className={T.mobileLabel}>VTHO Gas Fees Total</span>
                <span className={cn(T.mono)}>{formatTokens(r.gasFeeTotal)}</span>
              </div>
              <div className="space-y-1 text-right">
                <span className={T.mobileLabel}>VTHO Gas Fees ({displayYear})</span>
                <span className={cn(T.mono)}>{formatTokens(r.gasFeeYear)}</span>
              </div>

              <div className="space-y-1">
                <span className={T.mobileLabel}>Stargate Total</span>
                <span className={cn(T.mono)}>{formatTokens(r.stargateTotal)}</span>
              </div>
              <div className="space-y-1 text-right">
                <span className={T.mobileLabel}>Stargate ({displayYear})</span>
                <span className={cn(T.mono)}>{formatTokens(r.stargateYear)}</span>
              </div>
            </div>
          </div>
        ))}

        <div className={cn("rounded-2xl border px-4 py-4 text-sm font-semibold space-y-3", tone.footerPrimary)}>
          <div className="flex items-center gap-2">
            <span>📊</span>
            <span>{language === 'en' ? 'Totals' : 'Summen'}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <span className={cn(T.mobileLabel, "normal-case")}>{t.x2earnB3trMobileTotal}</span>
              <span className="font-mono">{formatTokens(totals.airdropTotal)}</span>
            </div>
            <div className="space-y-1 text-right">
              <span className={cn(T.mobileLabel, "normal-case")}>{t.x2earnB3trMobileYear}</span>
              <span className="font-mono">{formatTokens(totals.airdropYear)}</span>
            </div>

            <div className="space-y-1">
              <span className={cn(T.mobileLabel, "normal-case")}>B3TR Total</span>
              <span className="font-mono">{formatTokens(totals.govTotal)}</span>
            </div>
            <div className="space-y-1 text-right">
              <span className={cn(T.mobileLabel, "normal-case")}>B3TR ({displayYear})</span>
              <span className="font-mono">{formatTokens(totals.govYear)}</span>
            </div>

            <div className="space-y-1">
              <span className={cn(T.mobileLabel, "normal-case")}>VTHO Generated Total</span>
              <span className="font-mono">{formatTokens(totals.vthoGenTotal)}</span>
            </div>
            <div className="space-y-1 text-right">
              <span className={cn(T.mobileLabel, "normal-case")}>VTHO Generated ({displayYear})</span>
              <span className="font-mono">{formatTokens(totals.vthoGenYear)}</span>
            </div>

            <div className="space-y-1">
              <span className={cn(T.mobileLabel, "normal-case")}>VTHO Gas Fees Total</span>
              <span className="font-mono">{formatTokens(totals.gasFeeTotal)}</span>
            </div>
            <div className="space-y-1 text-right">
              <span className={cn(T.mobileLabel, "normal-case")}>VTHO Gas Fees ({displayYear})</span>
              <span className="font-mono">{formatTokens(totals.gasFeeYear)}</span>
            </div>

            <div className="space-y-1">
              <span className={cn(T.mobileLabel, "normal-case")}>Stargate Total</span>
              <span className="font-mono">{formatTokens(totals.stargateTotal)}</span>
            </div>
            <div className="space-y-1 text-right">
              <span className={cn(T.mobileLabel, "normal-case")}>Stargate ({displayYear})</span>
              <span className="font-mono">{formatTokens(totals.stargateYear)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200/80 bg-gradient-to-b from-white to-slate-50/60 px-6 py-4">
        <p className="text-sm leading-6 text-slate-600">
          💡 <strong className="text-slate-800">{language === 'en' ? 'Note:' : 'Hinweis:'}</strong> {language === 'en' ? 'All values are token amounts based on classified transactions.' : 'Alle Werte sind Token-Anzahlen basierend auf klassifizierten Transaktionen.'}{' '}
          <em>{language === 'en' ? 'Total' : 'Gesamt'}</em> = {language === 'en' ? 'Lifetime' : 'Lifetime'}, <em>({displayYear})</em> = {language === 'en' ? 'tax year only' : 'nur Steuerjahr'}.
        </p>
      </div>
    </section>
  );
}
